import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');
  const paymentIntentId = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('redirect_status');

  useEffect(() => {
    if (paymentIntentId) {
      // Embedded Payment Element flow — confirm via PaymentIntent
      pollPaymentIntent(paymentIntentId, 0);
    } else if (sessionId) {
      // Legacy Stripe Checkout redirect flow
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, paymentIntentId]);

  const pollPaymentIntent = async (pi, attempts) => {
    const maxAttempts = 6;
    const pollInterval = 1500;

    if (redirectStatus === 'failed') {
      setStatus('error');
      return;
    }
    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }
    try {
      const response = await fetch(`${API}/confirm-payment/${pi}`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setPaymentData({ amount_total: Math.round((data.amount || 0) * 100), currency: 'eur' });
      if (data.payment_status === 'paid' || data.status === 'succeeded' || data.payment_status === 'succeeded') {
        if (window.gtag) {
          window.gtag('event', 'purchase', {
            transaction_id: pi,
            value: data.amount || 0,
            currency: 'EUR',
          });
        }
        setStatus('success');
        return;
      }
      setTimeout(() => pollPaymentIntent(pi, attempts + 1), pollInterval);
    } catch (err) {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentIntent(pi, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/status/${sid}`);
      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        if (window.gtag) {
          window.gtag('event', 'purchase', {
            transaction_id: sid,
            value: (data.amount_total || 0) / 100,
            currency: (data.currency || 'eur').toUpperCase(),
          });
        }
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-5">
      <div className="text-center max-w-sm">
        {status === 'checking' && (
          <>
            <Loader2 size={64} className="text-[#F82C47] animate-spin mx-auto mb-4" />
            <h1 className="text-white text-2xl font-bold mb-2">Verificando pago...</h1>
            <p className="text-gray-400">Estamos confirmando tu pago con Stripe</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h1 className="text-white text-2xl font-bold mb-2">¡Pago exitoso!</h1>
            <p className="text-gray-400 mb-2">Tu entrada ha sido confirmada.</p>
            {paymentData && (
              <p className="text-[#F82C47] font-bold text-lg mb-6">
                {(paymentData.amount_total / 100).toFixed(2)}€
              </p>
            )}
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 rounded-full text-white font-bold"
              style={{ backgroundColor: '#F82C47' }}
            >
              Volver al inicio
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle size={64} className="text-red-500 mx-auto mb-4" />
            <h1 className="text-white text-2xl font-bold mb-2">Error en el pago</h1>
            <p className="text-gray-400 mb-6">No se pudo confirmar tu pago. Inténtalo de nuevo.</p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 rounded-full text-white font-bold"
              style={{ backgroundColor: '#F82C47' }}
            >
              Volver al inicio
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
